import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';

import { namedOperations } from '@sorare/core/src/__generated__/operations';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { formatGqlErrors } from '@sorare/core/src/lib/gql';

import type {
  DeleteSo5LineupMutation,
  DeleteSo5LineupMutationVariables,
} from './__generated__/useDeleteLineup.graphql.ts';

const DELETE_SO5_LINEUP_MUTATION = gql`
  mutation DeleteSo5LineupMutation($input: deleteSo5LineupInput!) {
    deleteSo5Lineup(input: $input) {
      currentUser {
        slug
        blockchainCardsInLineups
      }
      so5Leaderboard {
        slug
        so5Fixture {
          slug
          mySo5LineupsCount
          so5LeaderboardGroups {
            slug
            eligibleCardsCountByPosition {
              slug
              position
              seasonality
              totalCount
              usedCardsCount
            }
          }
        }
        so5League {
          slug
          mySo5LeaderboardContenders {
            slug
            so5Lineup {
              id
            }
            managerTeam {
              id
            }
          }
        }
        so5LeaderboardGroup {
          slug
          mySo5LeaderboardContenders {
            slug
            so5Lineup {
              id
            }
            managerTeam {
              id
            }
          }
        }
      }
      errors {
        path
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  DeleteSo5LineupMutation,
  DeleteSo5LineupMutationVariables
>;

export const useDeleteLineup = () => {
  const [deleteSo5Lineup] = useMutation(DELETE_SO5_LINEUP_MUTATION, {
    refetchQueries: [namedOperations.Query.HomeEventTypeQuery],
    update(cache, context, { variables }) {
      if (variables?.input.so5LineupId) {
        const lineupCacheId = cache.identify({
          __typename: 'So5Lineup',
          id: variables?.input.so5LineupId,
        });

        cache.evict({
          id: lineupCacheId,
        });
      }
    },
  });
  const { showNotification } = useSnackNotificationContext();

  return async (so5LineupId: string) => {
    const result = await deleteSo5Lineup({
      variables: {
        input: {
          so5LineupId,
        },
      },
    });

    const errors = result.data?.deleteSo5Lineup?.errors || [];

    if (errors.length) {
      showNotification('errors', { errors: formatGqlErrors(errors) });
      return errors;
    }
    showNotification('so5FixtureDelete');
    return null;
  };
};
